import type { TargetAndTransition } from 'framer-motion';

/**
 * Spring for chrome that has to settle fast with no overshoot (tab underline,
 * segmented-toggle thumb, badge count pops). Pass as `transition`.
 */
export const SNAPPY_SPRING = {
  type: 'spring',
  stiffness: 520,
  damping: 42,
  mass: 0.8,
} as const;

/** Ease-out bezier for every timed micro-interaction (120–200ms). */
export const EASE_OUT = [0.2, 0, 0, 1] as const;

/**
 * Enter/exit targets that work both spread onto a `motion.*` element
 * (`<motion.div {...popover} />`) and as `variants` with the matching labels.
 */
export interface MotionPreset {
  initial: TargetAndTransition;
  animate: TargetAndTransition;
  exit?: TargetAndTransition;
}

/** Popover/menu opening downward: fade + 0.97 scale + 4px drop, 160ms in / 110ms out. Pair with `origin-top`. */
export const popover: MotionPreset = {
  initial: { opacity: 0, scale: 0.97, y: -4 },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.16, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    y: -2,
    transition: { duration: 0.11, ease: EASE_OUT },
  },
};

/** Same as `popover` for menus that open upward (bottom-anchored). Pair with `origin-bottom`. */
export const popoverUp: MotionPreset = {
  initial: { opacity: 0, scale: 0.97, y: 4 },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.16, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    y: 2,
    transition: { duration: 0.11, ease: EASE_OUT },
  },
};

/** Page/section mount: fade + 6px rise over 200ms. No exit — pages just unmount. */
export const pageEnter: MotionPreset = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2, ease: EASE_OUT } },
};

/** Plain 150ms crossfade for content swaps (labels, tab panels, phase changes). */
export const fadeIn: MotionPreset = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.15, ease: EASE_OUT } },
  exit: { opacity: 0, transition: { duration: 0.15, ease: EASE_OUT } },
};

/**
 * Staggered row entrance for lists on first mount only (key on data identity so
 * refetches never replay it). Delay caps at the 9th row so long lists stay snappy.
 */
export const listItem = (index: number): MotionPreset => ({
  initial: { opacity: 0, y: 4 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.16,
      ease: EASE_OUT,
      delay: Math.min(index, 8) * 0.02,
    },
  },
});
